/**
 * Get Wishlist Action
 * Triggered by: arc:action:wishlistGet
 */

import shopifyHelper from "@js/utils/shopify";

// Global wishlist state
let wishlistProducts = JSON.parse(localStorage.getItem('wishlist_products') || '[]');

export const runWishlistGet = async (data = {}) => {
    try {
        const { 
            metaobjectId,
            fromLocalStorage = true,
            refreshFromShopify = false
        } = data;

        // If requesting from local storage or no metaobject ID provided
        if (fromLocalStorage || !metaobjectId) {
            window.dispatchEvent(new CustomEvent('wishlist:retrieved', {
                detail: {
                    products: wishlistProducts,
                    source: 'localStorage',
                    count: wishlistProducts.length,
                    timestamp: new Date().toISOString()
                }
            }));

            return {
                success: true,
                products: wishlistProducts,
                source: 'localStorage',
                count: wishlistProducts.length
            };
        }

        // If requesting fresh data from Shopify metaobject
        if (refreshFromShopify && metaobjectId) {
            const graphqlQuery = `query {
                metaobject(id: "${metaobjectId}") {
                    id
                    handle
                    onlineStoreUrl
                    updatedAt
                    fields {
                        key
                        value
                    }
                }
            }`;

            const response = await fetch(`${window.location.origin}/api/2024-01/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/graphql',
                    'X-Shopify-Storefront-Access-Token': '3ff61c2a868a43f2969f3a0f3fbf0173',
                    'X-Shopify-Access-Token': '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'
                },
                body: graphqlQuery
            });

            const jsonResponse = await response.json();
            
            if (jsonResponse.errors) {
                throw new Error(`GraphQL Error: ${JSON.stringify(jsonResponse.errors)}`);
            }

            // Extract product IDs from metaobject response
            const productIds = extractProductIds(jsonResponse);
            
            // Get full product data from Shopify
            const transformedProducts = await shopifyHelper.getShopifyProductList(productIds);
            
            // Update local storage and global state
            wishlistProducts = transformedProducts;
            localStorage.setItem('wishlist_products', JSON.stringify(wishlistProducts));

            window.dispatchEvent(new CustomEvent('wishlist:retrieved', {
                detail: {
                    products: transformedProducts,
                    source: 'shopify',
                    count: transformedProducts.length,
                    metaobjectId,
                    timestamp: new Date().toISOString()
                }
            }));

            window.dispatchEvent(new CustomEvent('wishlist:updated', {
                detail: {
                    products: transformedProducts,
                    count: transformedProducts.length,
                    action: 'refresh',
                    timestamp: new Date().toISOString()
                }
            }));

            return {
                success: true,
                products: transformedProducts,
                source: 'shopify',
                count: transformedProducts.length,
                metaobjectId
            };
        }

        return {
            success: true,
            products: wishlistProducts,
            source: 'localStorage',
            count: wishlistProducts.length
        };

    } catch (error) {
        console.error('Error getting wishlist:', error);
        
        window.dispatchEvent(new CustomEvent('wishlist:error', {
            detail: {
                error: error.message,
                action: 'get',
                timestamp: new Date().toISOString()
            }
        }));

        return {
            success: false,
            error: error.message,
            products: wishlistProducts, // Return cached products on error
            source: 'localStorage'
        };
    }
};

// Helper function to extract product IDs from metaobject response
const extractProductIds = (responseData) => {
    let fields = responseData.data.metaobject.fields;
    let productIds = [];

    fields.forEach(field => {
        if (field.key === 'products') {
            try {
                // Assuming the value is a JSON string of an array
                let products = JSON.parse(field.value);
                products.forEach(product => {
                    let matches = product.match(/Product\/(\d+)/);
                    if (matches) {
                        productIds.push(matches[1]);
                    }
                });
            } catch (e) {
                console.warn('Failed to parse products field:', field.value);
            }
        }
    });

    return productIds;
};

// Helper function to check if item is in wishlist
export const isItemInWishlist = (productId) => {
    const currentWishlist = JSON.parse(localStorage.getItem('wishlist_products') || '[]');
    return currentWishlist.some(product => parseInt(product.id) === parseInt(productId));
};

// Helper function to get wishlist count
export const getWishlistCount = () => {
    const currentWishlist = JSON.parse(localStorage.getItem('wishlist_products') || '[]');
    return currentWishlist.length;
};

// Helper function to initialize wishlist
export const initializeWishlist = () => {
    try {
        const stored = localStorage.getItem('wishlist_products');
        if (!stored) {
            localStorage.setItem('wishlist_products', JSON.stringify([]));
        }
        
        wishlistProducts = JSON.parse(localStorage.getItem('wishlist_products') || '[]');
        
        window.dispatchEvent(new CustomEvent('wishlist:initialized', {
            detail: {
                products: wishlistProducts,
                count: wishlistProducts.length,
                timestamp: new Date().toISOString()
            }
        }));

        return {
            success: true,
            products: wishlistProducts,
            count: wishlistProducts.length
        };

    } catch (error) {
        console.error('Error initializing wishlist:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
